import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { ConfigurationCode } from 'components/ConfigurationCode';
import { GlassConfig } from 'components/GlassConfig';
import GlassConfigUtil from 'components/GlassConfig/GlassConfigUtil';
import { TotalInfo } from 'components/TotalInfo';
import { UploadedDocuments } from 'components/UploadedDocuments';
import { YourReference } from 'components/YourReference';
import { AppDataContext } from 'context/appData';
import { AuthContext } from 'context/auth';
import { ConfigurationDataContext, GlassStructureContext } from 'context/configuration';
import ToastifyHandler from 'utils/ToastifyHandler';
import { CONFIGURATIONS_COLLECTION_NAME, USER_ROLES } from 'utils/constant';
import { firebaseDB } from 'utils/firebase';
import { Spinner } from 'components/Spinner';

const PreviewPage = () => {
	const { t } = useTranslation(['cartPage', 'firebaseErrorMessages']);
	const { configurationCode } = useParams();
	const notificationsHandler = useRef(new ToastifyHandler());
	const { configuratorData, isConfiguratorLoaded } = useContext(AppDataContext);
	const { currentUserAdditionalData, currentUserRole } = useContext(AuthContext);
	const { configuration, setConfiguration } = useContext(ConfigurationDataContext);
	const { uiHandlerRef } = useContext(GlassStructureContext);
	const [unimportedConfiguration, setUnimportedConfiguration] = useState(null);
	const [showPrice, setShowPrice] = useState(
		currentUserAdditionalData?.company?.showPrices || false
	);

	const importConfiguration = useCallback(
		cfg => {
			const c = GlassConfigUtil.importConfiguration(cfg, {
				updatePrices: false,
				uiHandlerRef,
				currentUserAdditionalData,
				configuratorData,
			});
			setUnimportedConfiguration(null);
			setConfiguration(c);
		},
		[
			configuratorData,
			currentUserAdditionalData,
			setConfiguration,
			setUnimportedConfiguration,
			uiHandlerRef,
		]
	);

	const getConfiguration = useCallback(async () => {
		notificationsHandler.current.pending(t('notifications:loadConfiguration'));
		const companyConfigurationsRef = collection(firebaseDB, CONFIGURATIONS_COLLECTION_NAME);

		try {
			let companyConfigurationQuery;

			if (currentUserRole === USER_ROLES.admin) {
				companyConfigurationQuery = query(
					companyConfigurationsRef,
					where('code', '==', configurationCode)
				);
			} else {
				companyConfigurationQuery = query(
					companyConfigurationsRef,
					where('companyId', '==', currentUserAdditionalData.companyId),
					where('code', '==', configurationCode)
				);
			}

			const querySnapshot = await getDocs(companyConfigurationQuery);

			let resConfiguration;

			// forEach is only one item since code is (hopefully) unique
			querySnapshot.forEach(doc => {
				resConfiguration = doc.data();
			});

			if (resConfiguration) {
				notificationsHandler.current.success(t('notifications:configurationUpdatedSuccessfully'));
				try {
					importConfiguration(resConfiguration);
				} catch (_e) {
					// db not ready .. handle in effect
					setUnimportedConfiguration(resConfiguration);
				}
			} else {
				notificationsHandler.current.rejected(t('notifications:configurationNotFound'));
			}
		} catch (error) {
			const { code } = error;
			notificationsHandler.current.rejected(t(code, { ns: 'firebaseErrorMessages' }));
		}
	}, [
		configurationCode,
		currentUserAdditionalData.companyId,
		currentUserRole,
		importConfiguration,
		t,
	]);

	useEffect(() => {
		setShowPrice(currentUserAdditionalData?.company?.showPrices || false);
	}, [currentUserAdditionalData]);

	useEffect(() => {
		if (!isConfiguratorLoaded || !unimportedConfiguration) return;
		importConfiguration(unimportedConfiguration);
	}, [importConfiguration, isConfiguratorLoaded, unimportedConfiguration]);

	useEffect(() => {
		if (configurationCode && currentUserAdditionalData) {
			getConfiguration();
		}
	}, [configurationCode, currentUserAdditionalData, getConfiguration]);

	if (!isConfiguratorLoaded || !configuration) {
		return <Spinner />;
	}

	return (
		<section className="section preview_page">
			<div className="section__in">
				<div className="section__block">
					<YourReference
						parent={configuration}
						values={{
							reference: configuration.reference,
							buildingReference: configuration.buildingReference,
						}}
					/>
				</div>
				{configuration.glassTypes && configuration.glassTypes.length
					? configuration.glassTypes.map((glassData, index) => {
							return (
								<div className="section__block" key={glassData.id || index}>
									<GlassConfig
										configurationData={configuration}
										glassTypeId={glassData.id}
										showPrice={showPrice}
										isPreview
									/>
								</div>
							);
						})
					: null}
				<div className="section__block">
					<TotalInfo {...configuration.totals.totalGlassSizesSum} showPrice={showPrice} />
				</div>
				<div className="section__block section_uploaded_docs_config_code">
					{configuration.uploadedDocuments.length > 0 && (
						<UploadedDocuments previewPage uploadedDocuments={configuration.uploadedDocuments} />
					)}
					<ConfigurationCode code={configuration.code} />
				</div>
			</div>
		</section>
	);
};

export default PreviewPage;
